/**
 * Matrix audit presets for the Matrix Immunity demo (RD-56).
 *
 * Each preset carries the four operating matrices the four-condition audit
 * inspects — one per generator stratum:
 *
 *   - authority (G1): a candidate Lie-subgroup element; A1 checks closure.
 *   - knowledge (G2): a (block-)diagonalizable operator; A2 checks that its
 *     eigenvalue clusters are separated by a gap.
 *   - temporal (G3): a temporal transition matrix M; A3 checks Tr(log M) = 0
 *     (equivalently det(M) = 1, a volume-preserving transition).
 *   - resourceGenerator (G4): a resource generator X; A4 checks the master
 *     identity exp(Tr(X)) = det(exp(X)).
 *
 * The presets are genuine elements of Mₙ(ℂ): each operating matrix is a
 * `ComplexMatrix` built from the real `@csk/core` realization via `cmatFromReal`.
 * The audit is dimension-general, so the presets mix GL(2,ℂ) and GL(3,ℂ)
 * systems — a 2×2 preset and a 3×3 preset are audited through the very same
 * complex det / trace / matrix-exponential / eigenvalue primitives. Every preset
 * here keeps imaginary parts zero (the running examples are real), but the audit
 * runs through the complex GL(n,ℂ) operations, so the presets are data in the
 * same type the certifier consumes — one source of truth shared by the audit lab
 * and the dataset tests. The `expected` field records the audit outcome each
 * preset is designed to exhibit so the tests can pin them.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { cmatFromReal, type ComplexMatrix } from "@csk/core/linear";

/**
 * Operating matrices are GL(n,ℂ) elements. The demo re-exports the @csk/core
 * `ComplexMatrix` type under the name `Matrix` for readability at the call
 * sites.
 */
export type Matrix = ComplexMatrix;
export type { ComplexMatrix } from "@csk/core/linear";

export type ConditionId = "A1" | "A2" | "A3" | "A4";

export interface MatrixPreset {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  /** G1 authority candidate. */
  readonly authority: Matrix;
  /** G2 knowledge operator (spectral separation source). */
  readonly knowledge: Matrix;
  /** G3 temporal transition matrix M (A3 inspects Tr(log M)). */
  readonly temporal: Matrix;
  /** G4 resource generator X (A4 inspects exp(Tr X) vs det(exp X)). */
  readonly resourceGenerator: Matrix;
  /** Conditions this preset is designed to satisfy. */
  readonly expected: {
    readonly immune: boolean;
    /** Conditions expected to fail (empty for the immune preset). */
    readonly failing: readonly ConditionId[];
    /** True when the resource block is engineered to approach the singular boundary. */
    readonly nearSingular: boolean;
  };
}

export const MATRIX_PRESETS: readonly [MatrixPreset, ...MatrixPreset[]] = [
  {
    id: "two-tenant-api",
    title: "Two-tenant API (immune)",
    summary:
      "The RD-56 §0.5 running example in GL(2,ℂ): two service accounts over two resource pools. Diagonal authority is a Lie subgroup, the eigenvalues separate, the temporal transition is volume-preserving, and the resource accounts balance. All four conditions hold — immune by construction.",
    authority: cmatFromReal([
      [2, 0],
      [0, 1],
    ]),
    knowledge: cmatFromReal([
      [2, 0],
      [0, 3],
    ]),
    temporal: cmatFromReal([
      [Math.E, 0],
      [0, 1 / Math.E],
    ]),
    resourceGenerator: cmatFromReal([
      [0.5, 0],
      [0, -0.5],
    ]),
    expected: { immune: true, failing: [], nearSingular: false },
  },
  {
    id: "rotation-flow",
    title: "Rotation flow (immune)",
    summary:
      "A volume-preserving rotation-style governance system in GL(2,ℂ). The authority block is diagonal, the knowledge spectrum is well separated, and the temporal transition has det = 1 (Tr(log M) = 0). All four conditions hold.",
    authority: cmatFromReal([
      [3, 0],
      [0, 0.5],
    ]),
    knowledge: cmatFromReal([
      [1, 0],
      [0, 5],
    ]),
    temporal: cmatFromReal([
      [2, 0],
      [0, 0.5],
    ]),
    resourceGenerator: cmatFromReal([
      [1, 0],
      [0, -1],
    ]),
    expected: { immune: true, failing: [], nearSingular: false },
  },
  {
    id: "temporal-leak",
    title: "Temporal leak (fails A3)",
    summary:
      "Authority, knowledge, and resources are all well-formed, but the temporal transition has det ≠ 1 — Tr(log M) ≠ 0. The temporal flow is not volume-preserving: a replay / temporal-bypass path becomes expressible. PRECOMMIT is no longer structurally guaranteed.",
    authority: cmatFromReal([
      [2, 0],
      [0, 1],
    ]),
    knowledge: cmatFromReal([
      [1, 0],
      [0, 4],
    ]),
    temporal: cmatFromReal([
      [3, 0],
      [0, 2],
    ]),
    resourceGenerator: cmatFromReal([
      [0.5, 0],
      [0, -0.5],
    ]),
    expected: { immune: false, failing: ["A3"], nearSingular: false },
  },
  {
    id: "domain-overlap",
    title: "Domain overlap (fails A2)",
    summary:
      "The knowledge operator's eigenvalues sit on top of each other — the eigenvalue clusters are not separated by a gap, so information domains are not cleanly block-diagonal. A cross-domain data path becomes expressible: SEPARATE is no longer structurally guaranteed.",
    authority: cmatFromReal([
      [2, 0],
      [0, 1],
    ]),
    knowledge: cmatFromReal([
      [2, 0],
      [0, 2.0001],
    ]),
    temporal: cmatFromReal([
      [Math.E, 0],
      [0, 1 / Math.E],
    ]),
    resourceGenerator: cmatFromReal([
      [0.5, 0],
      [0, -0.5],
    ]),
    expected: { immune: false, failing: ["A2"], nearSingular: false },
  },
  {
    id: "pre-singular-resource",
    title: "Pre-singular resource (det → 0)",
    summary:
      "The system is drifting toward the singular boundary: the resource generator has a large negative trace, so det(exp X) is collapsing toward 0. The system is approaching M(n) \\ GL(n), where the resource obligation becomes unresolvable. The pre-singularity warning fires before the guarantee lapses.",
    authority: cmatFromReal([
      [2, 0],
      [0, 1],
    ]),
    knowledge: cmatFromReal([
      [1, 0],
      [0, 4],
    ]),
    temporal: cmatFromReal([
      [Math.E, 0],
      [0, 1 / Math.E],
    ]),
    resourceGenerator: cmatFromReal([
      [-7, 0],
      [0, -7],
    ]),
    expected: { immune: true, failing: [], nearSingular: true },
  },
  {
    id: "three-tenant-mesh",
    title: "Three-tenant mesh (immune, GL(3,ℂ))",
    summary:
      "The RD-56 running example lifted to GL(3,ℂ): three service accounts over three resource pools. Authority is the positive diagonal diag(2, 1, 0.5) — a Lie subgroup; the knowledge spectrum diag(1, 3, 7) has three well-separated eigenvalues; the temporal transition is the unipotent [[1,2,3],[0,1,4],[0,0,1]] with det = 1 (volume-preserving); and the traceless resource generator diag(1, −1, 0) balances exp(Tr X) = det(exp X). All four conditions hold across three dimensions — immune by construction.",
    authority: cmatFromReal([
      [2, 0, 0],
      [0, 1, 0],
      [0, 0, 0.5],
    ]),
    knowledge: cmatFromReal([
      [1, 0, 0],
      [0, 3, 0],
      [0, 0, 7],
    ]),
    temporal: cmatFromReal([
      [1, 2, 3],
      [0, 1, 4],
      [0, 0, 1],
    ]),
    resourceGenerator: cmatFromReal([
      [1, 0, 0],
      [0, -1, 0],
      [0, 0, 0],
    ]),
    expected: { immune: true, failing: [], nearSingular: false },
  },
  {
    id: "mesh-domain-overlap",
    title: "Mesh domain overlap (fails A2, GL(3,ℂ))",
    summary:
      "The three-tenant mesh with two knowledge eigenvalues collapsed onto each other: the spectrum diag(3, 3, 7) has a zero gap between its first two clusters, so two information domains are not cleanly block-diagonal. Authority, temporal, and resource blocks stay well-formed — only A2 fails. A cross-domain data path becomes expressible in GL(3,ℂ): SEPARATE is no longer structurally guaranteed.",
    authority: cmatFromReal([
      [2, 0, 0],
      [0, 1, 0],
      [0, 0, 0.5],
    ]),
    knowledge: cmatFromReal([
      [3, 0, 0],
      [0, 3, 0],
      [0, 0, 7],
    ]),
    temporal: cmatFromReal([
      [1, 2, 3],
      [0, 1, 4],
      [0, 0, 1],
    ]),
    resourceGenerator: cmatFromReal([
      [1, 0, 0],
      [0, -1, 0],
      [0, 0, 0],
    ]),
    expected: { immune: false, failing: ["A2"], nearSingular: false },
  },
];

export const DEFAULT_PRESET_ID: string = MATRIX_PRESETS[0].id;

export function presetById(id: string): MatrixPreset {
  return MATRIX_PRESETS.find((p) => p.id === id) ?? MATRIX_PRESETS[0];
}
